import React, { useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { Box, IconButton, Typography } from "@mui/material";
import { Alert } from "@components/Alert/Alert";
import Badge from "@components/Badge/Badge";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import FlowActionButton from "@components/FlowActionButtons/FlowActionButton";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { PartyType } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { DRYBULK_CARGO_OPS_LANG_BASE, SEQUENCE_EDITABLE_STATUSES } from "../constants";
import ExecutionDialogs from "../execution/ExecutionDialogs";
import ExecutionProvider from "../execution/ExecutionProvider";
import SequenceExecutionButtons from "../execution/SequenceExecutionButtons";
import { useCargoOperationDetail } from "../hooks/useCargoOperationDetail";
import { useIsCargoOpsShippingAgent } from "../hooks/useIsCargoOpsShippingAgent";
import { usePollingGate } from "../hooks/usePollingGate";
import { useRemoveSequence } from "../hooks/useRemoveSequence";
import { sequenceStatusLabel } from "../utils/sequenceStatusLabel";
import { operationStatusBadgeType } from "../utils/statusBadgeType";
import IncrementsTable from "./IncrementsTable";
import PartyPanel from "./PartyPanel";
import RemoveSequenceDialog from "./RemoveSequenceDialog";
import SequenceDetailsCard from "./SequenceDetailsCard";
import SequencePanel from "./SequencePanel/SequencePanel";

const LANG_BASE = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences`;

const SequenceDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { operationId, sequenceId } = useParams<{ operationId: string; sequenceId: string }>();
  const isOperationsUser = useAuthStore((state) => state.isOperationsUser);
  const isShippingAgent = useIsCargoOpsShippingAgent();
  const gate = usePollingGate();
  const [updating, setUpdating] = useState(false);
  const [party, setParty] = useState<{ code: string; type: PartyType } | null>(null);
  const [leaving, setLeaving] = useState(false);
  const remove = useRemoveSequence(operationId ?? "");
  const detail = useCargoOperationDetail(operationId, {
    paused: gate.busy || updating || remove.removing !== null || party !== null,
  });

  if (!isOperationsUser && !isShippingAgent) {
    return <Navigate to="../.." relative="path" replace />;
  }
  if (leaving) {
    return null;
  }

  const goBack = () => navigate("../..", { relative: "path" });
  const operationStatus = detail.data?.status ?? null;
  const sequence = detail.data?.sequences?.find((candidate) => candidate.id === sequenceId);
  const showSequenceActions =
    isShippingAgent && SEQUENCE_EDITABLE_STATUSES.includes(operationStatus ?? "");

  const confirmRemove = async () => {
    setLeaving(true);
    const removed = await remove.confirm(goBack);
    if (!removed) {
      setLeaving(false);
    }
  };

  const renderBody = () => {
    if (detail.isLoading) {
      return (
        <div className="relative min-h-[240px]">
          <LoadingOverlay loading testId="sequence-details-loading" />
        </div>
      );
    }
    if (detail.isError || !detail.data) {
      return <ErrorHandler error={detail.error} testId="sequence-details-load-error" />;
    }
    if (!sequence) {
      return (
        <div data-testid="sequence-details-not-found">
          <Alert type="info">{t(`${LANG_BASE}.sequence-page.not-found`)}</Alert>
        </div>
      );
    }
    return (
      <>
        <SequenceDetailsCard
          sequence={sequence}
          onOpenShipper={(code) => setParty({ code, type: "SHIPPER" })}
        />
        {sequence.increments && <IncrementsTable increments={sequence.increments} />}
      </>
    );
  };

  return (
    <ExecutionProvider>
      <Box
        data-testid="sequence-details-page"
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          <IconButton
            size="medium"
            sx={{ color: "text.primary", mr: 1 }}
            aria-label={t("common.back")}
            data-testid="sequence-details-back"
            onClick={goBack}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          {sequence && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography variant="h5" component="h2" data-testid="sequence-details-title">
                {t(`${LANG_BASE}.sequence-page.title`, { number: sequence.sequenceNo })}
              </Typography>
              <Badge
                type={operationStatusBadgeType(sequence.status)}
                label={sequenceStatusLabel(t, sequence.status)}
                testId="sequence-details-status-badge"
                labelTestId="sequence-details-status"
              />
            </Box>
          )}
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }} data-testid="sequence-details-actions">
            {sequence && showSequenceActions && (
              <>
                <FlowActionButton
                  langBase={LANG_BASE}
                  labelKey="remove.title"
                  testId="sequence-details-remove"
                  variant="outlined"
                  isActionAvailable
                  startIcon={<HighlightOffOutlinedIcon />}
                  onClick={() => remove.request(sequence)}
                />
                <FlowActionButton
                  langBase={LANG_BASE}
                  labelKey="grid.update"
                  testId="sequence-details-update"
                  variant="outlined"
                  isActionAvailable
                  startIcon={<AutorenewIcon />}
                  onClick={() => setUpdating(true)}
                />
              </>
            )}
            {sequence && isOperationsUser && (
              <SequenceExecutionButtons sequence={sequence} operationStatus={operationStatus} />
            )}
          </Box>
        </Box>
        {renderBody()}
      </Box>
      {sequence && updating && (
        <SequencePanel
          key={sequence.id}
          operationId={operationId ?? ""}
          sequence={sequence}
          onClose={() => setUpdating(false)}
        />
      )}
      {party && operationId && (
        <PartyPanel
          operationId={operationId}
          code={party.code}
          type={party.type}
          onClose={() => setParty(null)}
        />
      )}
      <RemoveSequenceDialog
        sequence={remove.removing}
        isSubmitting={remove.pending}
        onConfirm={() => void confirmRemove()}
        onCancel={remove.cancel}
      />
      {operationId && isOperationsUser && <ExecutionDialogs operationId={operationId} />}
    </ExecutionProvider>
  );
};

export default SequenceDetailsPage;
